import { useState, useCallback } from "react";
import axios from "axios";
import {
  FileText, Sparkles, Upload, Loader, AlertTriangle, CheckCircle,
  Shield, Zap, DollarSign, Award, AlertCircle, Code, X
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE = `apiVersion: apps/v1
kind: Deployment
metadata:
  name: my-app
spec:
  replicas: 1
  selector:
    matchLabels:
      app: my-app
  template:
    metadata:
      labels:
        app: my-app
    spec:
      containers:
      - name: app
        image: nginx:latest
        ports:
        - containerPort: 80
`;

const severityStyle = {
  critical: { text: "text-rose-400", bg: "bg-rose-400/10", border: "border-rose-400/20" },
  high:     { text: "text-orange-400", bg: "bg-orange-400/10", border: "border-orange-400/20" },
  medium:   { text: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/20" },
  low:      { text: "text-blue-400", bg: "bg-blue-400/10", border: "border-blue-400/20" },
};

const categoryIcon = {
  security: Shield,
  reliability: AlertCircle,
  cost: DollarSign,
  "best-practice": Award,
  syntax: Code,
};

function IssueCard({ issue }) {
  const s = severityStyle[issue.severity] || severityStyle.low;
  const Icon = categoryIcon[issue.category] || AlertTriangle;
  return (
    <div data-testid="manifest-issue-card" className={cn("border rounded-md p-3 animate-fade-in", s.border, s.bg)}>
      <div className="flex items-start gap-3">
        <Icon size={14} className={cn("flex-shrink-0 mt-0.5", s.text)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={cn("text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase", s.text, s.border)}>
              {issue.severity}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700 uppercase">
              {issue.category}
            </span>
            <p className="text-sm font-semibold text-slate-100">{issue.title}</p>
          </div>
          <p className="text-xs text-slate-400 mt-1.5">{issue.description}</p>
          {issue.fix && (
            <div className="mt-2 px-3 py-2 rounded bg-emerald-400/5 border border-emerald-400/15">
              <p className="text-[10px] uppercase tracking-widest text-emerald-500 mb-0.5">Fix</p>
              <p className="text-xs text-emerald-300">{issue.fix}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScoreRing({ score }) {
  const color = score >= 80 ? "#34d399" : score >= 60 ? "#fbbf24" : score >= 40 ? "#fb923c" : "#f87171";
  const circumference = 2 * Math.PI * 28;
  const dash = (score / 100) * circumference;
  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg width="80" height="80" className="-rotate-90">
        <circle cx="40" cy="40" r="28" fill="none" stroke="#1e293b" strokeWidth="6" />
        <circle cx="40" cy="40" r="28" fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round" className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-semibold font-mono" style={{ color }}>{score}</span>
        <span className="text-[8px] uppercase tracking-widest text-slate-500">score</span>
      </div>
    </div>
  );
}

export default function ManifestAnalyzer() {
  const [yamlText, setYamlText] = useState(SAMPLE);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAnalyze = useCallback(async () => {
    if (!yamlText.trim()) {
      setError("Please paste a manifest");
      return;
    }
    setError(null); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/manifests/analyze`, { yaml_content: yamlText }, { withCredentials: true });
      setAnalysis(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  }, [yamlText]);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 200_000) {
      setError("File too large (max 200KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setYamlText(e.target.result);
    reader.readAsText(file);
  };

  const issueCounts = analysis?.issues?.reduce((acc, i) => {
    acc[i.severity] = (acc[i.severity] || 0) + 1;
    return acc;
  }, {}) || {};

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          Manifest Analyzer
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Paste or upload a Kubernetes YAML manifest. Claude reviews it for security, reliability, cost & best-practice issues.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Editor */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-200">YAML Manifest</h3>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs px-2.5 py-1 rounded border border-slate-700 text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition-colors cursor-pointer flex items-center gap-1.5">
                <Upload size={11} /> Upload
                <input
                  data-testid="manifest-file-input"
                  type="file"
                  accept=".yaml,.yml,text/yaml,application/x-yaml,text/plain"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </label>
              <button
                data-testid="load-sample-btn"
                onClick={() => setYamlText(SAMPLE)}
                className="text-xs px-2.5 py-1 rounded border border-slate-700 text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition-colors"
              >
                Sample
              </button>
            </div>
          </div>

          <textarea
            data-testid="manifest-yaml-input"
            value={yamlText}
            onChange={(e) => setYamlText(e.target.value)}
            placeholder="apiVersion: apps/v1&#10;kind: Deployment&#10;..."
            spellCheck={false}
            className="w-full h-[480px] font-mono text-xs px-3 py-3 bg-slate-950 border border-slate-800 rounded-md text-slate-200 focus:border-indigo-500/60 focus:outline-none resize-none"
            style={{ tabSize: 2 }}
          />

          <button
            data-testid="analyze-manifest-btn"
            onClick={handleAnalyze}
            disabled={loading || !yamlText.trim()}
            className={cn(
              "w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm transition-all",
              loading ? "bg-slate-800 text-slate-500" : "bg-indigo-500 hover:bg-indigo-400 text-white shadow-md shadow-indigo-500/20"
            )}
          >
            {loading ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {loading ? "Analyzing with Claude…" : "Analyze with AI"}
          </button>

          {error && (
            <div data-testid="analyze-error" className="px-3 py-2 rounded bg-rose-400/10 border border-rose-400/20 text-rose-400 text-xs flex items-start gap-2">
              <X size={12} className="mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}
        </div>

        {/* Analysis */}
        <div className="space-y-3">
          {!analysis && !loading && (
            <div className="bg-slate-900/30 border border-slate-800 rounded-md p-10 text-center h-[540px] flex flex-col items-center justify-center">
              <Sparkles size={28} className="mx-auto mb-3 text-slate-600" />
              <p className="text-sm text-slate-400 mb-1">No analysis yet</p>
              <p className="text-xs text-slate-600">Click "Analyze with AI" to get a security & reliability review</p>
            </div>
          )}

          {loading && (
            <div className="bg-slate-900/30 border border-slate-800 rounded-md p-10 text-center h-[540px] flex flex-col items-center justify-center">
              <Loader size={24} className="mx-auto mb-3 text-indigo-400 animate-spin" />
              <p className="text-sm text-slate-400">Claude is reviewing your manifest…</p>
              <p className="text-xs text-slate-600 mt-1">Usually takes 3-8 seconds</p>
            </div>
          )}

          {analysis && (
            <div data-testid="manifest-analysis-results" className="space-y-3">
              {/* Score header */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 flex items-center gap-4 animate-fade-in">
                <ScoreRing score={analysis.score ?? 50} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Summary</p>
                  <p className="text-sm text-slate-200">{analysis.summary}</p>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500 flex-wrap">
                    {Object.entries(issueCounts).map(([sev, count]) => {
                      const s = severityStyle[sev] || severityStyle.low;
                      return (
                        <span key={sev} className={cn("px-1.5 py-0.5 rounded border font-mono uppercase", s.text, s.border)}>
                          {count} {sev}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Resources detected */}
              {analysis.resources?.length > 0 && (
                <div className="bg-slate-900/50 border border-slate-800 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Resources Detected</p>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.resources.map((r, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300 font-mono">
                        {r.kind} · {r.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Issues */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={13} className="text-amber-400" />
                  <h3 className="text-xs font-semibold text-slate-200 uppercase tracking-widest">Issues ({analysis.issues?.length || 0})</h3>
                </div>
                {(!analysis.issues || analysis.issues.length === 0) ? (
                  <div className="bg-emerald-400/5 border border-emerald-400/20 rounded p-3 text-center text-sm text-emerald-400 flex items-center justify-center gap-2">
                    <CheckCircle size={14} /> No issues detected
                  </div>
                ) : (
                  analysis.issues.map((iss, i) => <IssueCard key={i} issue={iss} />)
                )}
              </div>

              {/* Recommendations */}
              {analysis.recommendations?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Award size={13} className="text-emerald-400" />
                    <h3 className="text-xs font-semibold text-slate-200 uppercase tracking-widest">Recommendations</h3>
                  </div>
                  <div className="bg-slate-900/50 border border-slate-800 rounded-md p-3 space-y-1.5">
                    {analysis.recommendations.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-slate-300">
                        <Zap size={10} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
